import qs from 'qs';

import { getWindow, getWindowSafe } from '@change-corgi/core/window';

export { getShareUrlInfo, type ShareUrlInfo } from './getShareUrlInfo';
export { isShareCancellation } from './isShareCancellation';

type NavigatorShare = {
	share: Window['navigator']['share'];
	canShare: Window['navigator']['canShare'];
};

const SIMULATION: NavigatorShare = {
	share: async (shareData) => {
		const details = [
			shareData?.title && `Title: ${shareData.title}`,
			shareData?.text && `Text: ${shareData.text}`,
			shareData?.url && `URL: ${shareData.url}`,
			shareData?.files?.length && `Files: ${shareData.files.map((file) => file.name).join(', ')}`,
		].filter(Boolean);
		// eslint-disable-next-line no-alert
		alert(`Simulated native share action. This is not a real share action.\n\n${details.join('\n')}`);
		return Promise.resolve();
	},
	canShare: () => true,
};

/**
 * returns an interface similar to the native navigator.share/canShare interface but with:
 * - undefined when the native share is not available
 * - a simulated share interface when native share is not available on the device (e.g. devtools) if the url contains `simulate_native_share=true`
 *
 * For instance, to test the share functionality in a browser that does not support the native share API,
 * you can add `?simulate_native_share=true` to the URL. This will display an alert instead of actually
 * sharing the content, but will allow you to test the share flow without having to use a real mobile device.
 *
 * The simulated share interface can also be enabled by setting `window.simulateNativeShare = true` in the console.
 */
export function getNavigatorShareInterface(): NavigatorShare | undefined {
	const window = getWindowSafe();
	if (!window) {
		return undefined;
	}
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	if (!window.navigator.canShare || !window.navigator.share) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
		if ((window as any).simulateNativeShare) {
			return SIMULATION;
		}
		const query = qs.parse(window.location.search, { ignoreQueryPrefix: true });
		if (query.simulate_native_share === 'true') {
			// persisting the simulation state in the window object in case the user navigates to another page
			// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
			(window as any).simulateNativeShare = true;
			return SIMULATION;
		}
		return undefined;
	}
	return window.navigator;
}

/**
 * Opens a URL by clicking a programmatic <a> element. This is how every share channel opens its
 * destination — prefer it over `window.open()` or `location.href` for all of them, not just custom
 * schemes. `window.open()` fails silently in WKWebView (Facebook's in-app browser) and on iOS
 * browsers other than Safari, and `location.href` may be blocked by WKWebView's navigation policy.
 * A programmatic <a> click reads as a user-originated navigation, so the webview honours it.
 *
 * Pass `newTab` for an http(s) destination the sharer reads and comes back from, so this tab keeps
 * the flow. Leave it off for a custom scheme (`sms:`, `line://`, `fb-messenger://`): the OS takes
 * the URL and no page ever loads, so a `_blank` target would strand an empty tab behind the app
 * switch.
 *
 * `newTab` relies on the tap's transient user activation to clear the popup blocker. One microtask
 * is fine: the share pipeline already awaits `onBeforeAction` in `actions.ts` before any channel
 * runs, and X ships through that path with a device-verified fix (CHANGE-85432). What costs the
 * activation is awaiting something that can settle later — a clipboard write, a network call — so
 * fire those without awaiting, as `reddit` and `line` do. `navigator.share()` is stricter than an
 * anchor and loses the activation across even a microtask; see `tiktok`/`instagramStories`.
 */
export function openUrlWithHiddenAnchor(url: string, options: { newTab?: boolean } = {}): void {
	const win = getWindow();
	const a = win.document.createElement('a');
	a.href = url;
	a.style.display = 'none';
	if (options.newTab) {
		a.target = '_blank';
		a.rel = 'noopener noreferrer';
	}
	win.document.body.appendChild(a);
	a.click();
	win.document.body.removeChild(a);
}
